import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { operatorConsole } from '../../lib/api/operatorConsole';
import { RecoveryCodesScreen } from '../Login/RecoveryCodesScreen';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';

type Step =
  | { name: 'idle' }
  | { name: 'qr'; otpauthUri: string; secret: string }
  | { name: 'recovery'; codes: string[] };

export function TwoFactorCard({ enrolled, onEnabled }: { enrolled: boolean; onEnabled: () => void }) {
  const [step, setStep] = useState<Step>({ name: 'idle' });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (step.name === 'qr' && canvasRef.current) {
      void QRCode.toCanvas(canvasRef.current, step.otpauthUri, { width: 192 });
    }
  }, [step]);

  if (enrolled) {
    return (
      <section className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Two-factor authentication</h2>
        <p className="text-sm text-[var(--co-success)]">
          Two-factor authentication is on for your sign-in.
        </p>
        <p className="text-sm text-[var(--co-muted)]">To disable or reset it, contact support.</p>
      </section>
    );
  }

  async function startEnroll() {
    setError(null);
    setPending(true);
    try {
      const { otpauth_uri, secret } = await operatorConsole.enrollSecurityOtp(password);
      setPassword('');
      setStep({ name: 'qr', otpauthUri: otpauth_uri, secret });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrollment failed');
    } finally {
      setPending(false);
    }
  }

  async function confirmEnroll() {
    setError(null);
    setPending(true);
    try {
      const { recovery_codes } = await operatorConsole.confirmSecurityOtp(code.trim());
      setCode('');
      setStep({ name: 'recovery', codes: recovery_codes });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-semibold">Two-factor authentication</h2>
        <p className="text-sm text-[var(--co-muted)]">
          Protects <strong>your own</strong> sign-in with an authenticator app — it changes
          nothing for your teammates. Once enabled, only support can turn it off.
        </p>
      </header>

      {step.name === 'idle' && (
        <>
          <FormField label="Current password" htmlFor="sec-2fa-pw" error={error ?? undefined}>
            <Input
              id="sec-2fa-pw"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>
          <div>
            <Button type="button" onClick={() => void startEnroll()} disabled={pending || password === ''}>
              {pending ? 'Starting…' : 'Enable 2FA'}
            </Button>
          </div>
        </>
      )}

      {step.name === 'qr' && (
        <>
          <p className="text-sm text-[var(--co-muted)]">
            Scan the QR code with an authenticator app, then enter the 6-digit code.
          </p>
          <canvas ref={canvasRef} className="mx-auto" aria-label="TOTP QR code" />
          <p className="text-xs text-[var(--co-muted)] break-all">
            Can't scan? Enter this key manually: <code>{step.secret}</code>
          </p>
          <FormField label="Authenticator code" htmlFor="sec-2fa-code" error={error ?? undefined}>
            <Input
              id="sec-2fa-code"
              autoComplete="one-time-code"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </FormField>
          <div>
            <Button type="button" onClick={() => void confirmEnroll()} disabled={pending || code === ''}>
              {pending ? 'Confirming…' : 'Confirm'}
            </Button>
          </div>
        </>
      )}

      {step.name === 'recovery' && <RecoveryCodesScreen codes={step.codes} onDone={onEnabled} />}
    </section>
  );
}
